export type OrderInjectionTimeOptions = {
  /** Today's store open — injection cannot happen before the store opens. */
  storeOpensAt?: Date;
  now?: Date;
};

/**
 * When the order can be released to the kitchen: pickup minus prep, but not before now or store open.
 */
export function getOrderInjectionTime(
  pickupTime: Date,
  earliestPickupTimeInSeconds: number,
  { storeOpensAt, now = new Date() }: OrderInjectionTimeOptions = {}
): Date {
  const scheduledInjectionMs =
    pickupTime.getTime() - Number(earliestPickupTimeInSeconds) * 1000;

  let injectionMs = Math.max(now.getTime(), scheduledInjectionMs);

  if (storeOpensAt) {
    injectionMs = Math.max(injectionMs, storeOpensAt.getTime());
  }

  return new Date(injectionMs);
}

/** Seconds from now until the order can be injected (released to kitchen). */
export function getSecondsUntilOrderInjection(
  pickupTime: Date,
  earliestPickupTimeInSeconds: number,
  options: OrderInjectionTimeOptions = {}
): number {
  const now = options.now ?? new Date();
  const injectionTime = getOrderInjectionTime(
    pickupTime,
    earliestPickupTimeInSeconds,
    options
  );

  return Math.max(0, Math.round((injectionTime.getTime() - now.getTime()) / 1000));
}

/**
 * ASAP when the order can be injected within the LD threshold (not merely picked up soon).
 * @see workspaces/frontend/src/pages/cart/future-order-time-card/future-order-time-card.tsx
 */
export function shouldShowAsapPickupTime(
  pickupTime: Date,
  asapThresholdMinutes: number,
  earliestPickupTimeInSeconds = 0,
  injectionOptions: OrderInjectionTimeOptions = {}
): boolean {
  if (asapThresholdMinutes <= 0) {
    return false;
  }

  const thresholdSeconds = asapThresholdMinutes * 60;
  const prepSeconds = Number(earliestPickupTimeInSeconds);

  // Minimum prep exceeds the ASAP window — injection cannot happen soon enough.
  if (prepSeconds > thresholdSeconds) {
    return false;
  }

  const secondsUntilInjection = getSecondsUntilOrderInjection(
    pickupTime,
    earliestPickupTimeInSeconds,
    injectionOptions
  );

  return secondsUntilInjection <= thresholdSeconds;
}

/**
 * Checkout summary only — ASAP on first load before the user has ever confirmed.
 * After confirm (or when the 5-minute filter advances the default), show the slot time.
 */
export function shouldShowAsapOnCheckoutSummary(
  pickupTime: Date,
  asapThresholdMinutes: number,
  earliestPickupTimeInSeconds: number,
  allowAsapLabel: boolean,
  injectionOptions: OrderInjectionTimeOptions = {}
): boolean {
  if (!allowAsapLabel) {
    return false;
  }

  return shouldShowAsapPickupTime(
    pickupTime,
    asapThresholdMinutes,
    earliestPickupTimeInSeconds,
    injectionOptions
  );
}
